<template>
  <div class="flex flex-col divide-y flex-grow">
    <div class="flex flex-col pb-6">
      <div
        class="rounded-lg border p-5 flex flex-col space-y-3"
        :class="demandContainerClass">
        <div
          class="
            flex flex-col md:flex-row md:items-start md:justify-between
            md:space-x-6 space-y-3 md:space-y-0
          ">
          <div
            class="
              flex flex-col sm:flex-row sm:items-start
              sm:space-x-4 space-y-3 sm:space-y-0
            ">
            <div
              class="iwaly-traffic-light"
              aria-label="Светофор спроса iWALY">
              <span
                v-for="lamp in demandTrafficLightLamps"
                :key="lamp.key"
                class="iwaly-traffic-light__lamp"
                :class="lamp.className"/>
            </div>

            <div class="flex flex-col space-y-2">
            <span
              class="
                text-12px leading-16px font-semibold uppercase
                tracking-wide
              "
              :class="demandBadgeClass">
              {{ demandBadgeText }}
            </span>

            <strong class="text-18px leading-22px text-primary">
              {{ demandTitle }}
            </strong>

            <p class="text-14px leading-20px text-inverse">
              {{ demandMessage }}
            </p>
          </div>
          </div>

          <div
            v-if="demandStatusPending"
            class="text-13px leading-18px text-muted flex-shrink-0">
            Анализируем спрос…
          </div>
        </div>

        <div
          v-if="demandMetrics.length"
          class="iwaly-demand-metrics">
          <div class="iwaly-demand-metrics__title">
            Оценка iWALY по выбранным направлениям
          </div>

          <div class="iwaly-demand-metrics__list">
            <div
              v-for="metric in demandMetrics"
              :key="metric.label"
              class="iwaly-demand-metrics__row">
              <span class="iwaly-demand-metrics__label">
                {{ metric.label }}
              </span>
              <strong class="iwaly-demand-metrics__value">
                {{ metric.value }}
              </strong>
            </div>
          </div>

          <p class="iwaly-demand-metrics__note">
            {{ demandPromotionNote }}
          </p>
        </div>

        <div
          v-if="demandStatusError"
          class="iwaly-demand-helper">
          Не удалось автоматически получить статус спроса. Заявку всё равно
          можно отправить: мы проверим регион и направление вручную.
        </div>
      </div>
    </div>
    <div class="flex flex-col">
      <div class="flex flex-col divide-y">
        <div
          class="flex flex-row space-x-6 py-6 justify-between"
          :class="categoryRowClass(category)"
          v-for="(category, categoryIndex) in payload"
          :key="categoryIndex">
          <div
            class="
              flex flex-col flex-grow space-y-6
              sm:space-y-0 sm:flex-row sm:space-x-6
            ">
            <Attachment
              :id="category.iconAttach"
              class="object-contain object-top w-6"/>
            <div class="flex flex-col flex-grow">
              <span
                v-if="isVolunteerCategory(category)"
                class="
                  inline-flex items-center self-start mb-2 px-3 py-1
                  rounded-full border border-red-300 bg-red-50
                  text-red-700 text-12px leading-16px font-semibold
                ">
                Особая категория · подключаем сразу
              </span>

              <span class="text-16 leading-24px">{{ category.name }}</span>
              <span
                v-if="isVolunteerCategory(category)"
                class="mt-1 text-13px leading-18px text-muted max-w-2xl">
                Если выбираете только «Помощь волонтёров», регистрируем сразу.
                Категория выделена отдельно, потому что подключается
                автоматически. Это резервный формат: заказов может быть мало,
                особенно в регионах.
              </span>
              <span
                class="text-14px leading-16px text-primary hover:underline cursor-pointer"
                @click="openDetails(category)">
                Подробнее
              </span>
            </div>
          </div>

          <div class="self-center w-24 flex-shrink-0">
            <Check
              :value="category.value"
              :label="category.value ? 'Готов!' : 'Не готов'"
              @input="($event) => checkCategory(category, $event)"/>
          </div>
        </div>
      </div>
    </div>

    <modal
      name="service-category-details"
      :adaptive="true"
      :scrollable="true"
      width="640px"
      height="auto">
      <div class="p-8 flex flex-col space-y-6">
        <div class="flex flex-row justify-between items-start space-x-4">
          <h3 class="text-20px leading-24px font-semibold text-primary">
            {{ detailsTitle }}
          </h3>

          <button
            class="text-24px leading-24px text-muted"
            @click="$modal.hide('service-category-details')">
            ×
          </button>
        </div>

        <div class="text-14px leading-20px text-inverse whitespace-pre-line">
          {{ detailsText }}
        </div>

        <div class="flex justify-end">
          <button
            class="btn bg-primary border-solid border-primary text-white w-40"
            @click="$modal.hide('service-category-details')">
            Понятно
          </button>
        </div>
      </div>
    </modal>

    <div class="flex flex-col lg:flex-row space-y-8 lg:space-y-0 lg:space-x-8 pt-8 flex-grow">
      <div class="flex flex-col justify-end">
        <Check
          label="Согласие с документами iWALY"
          :value="policy"
          @input="($event) => policy = $event">
          <template v-slot:label>
            <span class="label">
              Я даю согласие на обработку персональных данных, подтверждаю
              достоверность указанных сведений, а также принимаю условия
              <span
                class="text-primary hover:underline cursor-pointer"
                @click.stop="openPolicy">
                Политики обработки и защиты персональных данных</span>,
              <span
                class="text-primary hover:underline cursor-pointer"
                @click.stop="openLicense">
                Лицензионного договора</span>
              и
              <span
                class="text-primary hover:underline cursor-pointer"
                @click.stop="openPublic">
                Публичной оферты на заключение договора оказания услуг</span>.
            </span>
          </template>
        </Check>
      </div>

      <div class="
        flex flex-col sm:flex-row flex-grow sm:items-end
        justify-end sm:space-x-6 space-y-6 sm:space-y-0
      ">
        <button
          class="
            btn border-solid border-primary text-primary
            flex-grow lg:flex-grow-0 sm:w-64
          "
          @click="$emit('prev')">
          Назад
        </button>

        <button
          class="
            btn bg-primary border-solid border-primary
            text-white flex-grow lg:flex-grow-0 sm:w-64
          "
          @click="onSend()">
          Завершить
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import { mapState, mapActions } from 'vuex';
import Attachment from '@/components/global/Attachment.vue';
import Check from '@/components/global/Check.vue';
import { contractorDemandStatus } from '@/api/data';

const SERVICE_DESCRIPTIONS = {
  grave_cleaning: {
    title: 'Уборка захоронения',
    text: [
      'Уборка захоронения — это разовые или регулярные работы по уходу за местом памяти.',
      '',
      'В зависимости от состояния участка заказ может включать базовую, расширенную или более глубокую уборку:',
      '• очистку участка от мусора, сухой травы, веток, листьев и старых цветов;',
      '• уборку проходов вокруг захоронения;',
      '• влажную чистку памятника и других элементов;',
      '• удаление сорняков;',
      '• уход за цветником, чистку ограды и мелкое приведение участка в порядок;',
      '• фотоотчёт после выполнения.',
      '',
      'Эта категория подходит исполнителям, которые готовы аккуратно и бережно выполнять стандартные работы по уходу за захоронениями.',
    ].join('\n'),
  },
  funeral_flowers_delivery: {
    title: 'Доставка траурных цветов',
    text: [
      'Доставка траурных цветов — это покупка, доставка и аккуратное размещение цветов или траурной композиции на месте захоронения.',
      '',
      'Обычно заказ может включать:',
      '• покупку или получение цветов;',
      '• доставку на кладбище;',
      '• размещение цветов на захоронении;',
      '• учёт пожеланий заказчика по цвету, количеству и композиции;',
      '• фотоотчёт после выполнения.',
      '',
      'В сезон такие заказы могут включать не только срезанные цветы, но и посадку живых растений, если это доступно в регионе.',
    ].join('\n'),
  },
  improvement_repair: {
    title: 'Благоустройство и ремонт',
    text: [
      'Благоустройство и ремонт — это более сложные работы по приведению захоронения в порядок, восстановлению или обновлению отдельных элементов.',
      '',
      'Примеры работ:',
      '• покраска или освежение ограды, столика, лавочки;',
      '• мелкий ремонт по запросу;',
      '• работы с цветником, плиткой, цоколем или элементами участка;',
      '• обновление внешнего вида захоронения;',
      '• подготовка участка к более крупным работам.',
      '',
      'Эта категория подходит исполнителям с опытом практических работ, аккуратностью и пониманием особенностей кладбищенской территории.',
    ].join('\n'),
  },
  custom_order: {
    title: 'Индивидуальный заказ',
    text: [
      'Индивидуальный заказ — это нестандартное поручение, которое не укладывается в обычные категории.',
      '',
      'Заказчик сам описывает, что нужно сделать, а исполнитель оценивает возможность выполнения, объём работ и условия.',
      '',
      'Это могут быть комбинированные задачи, особые пожелания заказчика, разовые поручения на кладбище или рядом с ним, работы с индивидуальным бюджетом и задачи, которые требуют предварительного уточнения.',
      '',
      'Главное — внимательно читать комментарий заказчика и заранее уточнять детали, если задача описана недостаточно ясно.',
    ].join('\n'),
  },
  grave_search: {
    title: 'Услуги поиска',
    text: [
      'Услуги поиска — это поиск, проверка или верификация захоронения по имеющимся данным.',
      '',
      'Сложность может быть разной: от простой проверки известного участка до поиска по частичным сведениям.',
      '',
      'Заказ может включать:',
      '• поиск захоронения по ФИО, датам жизни, кладбищу или другим данным;',
      '• верификацию найденного места;',
      '• фотофиксацию захоронения;',
      '• уточнение расположения и ориентиров;',
      '• передачу заказчику результата и, при возможности, геопозиции.',
      '',
      'Эта категория подходит тем, кто хорошо ориентируется на местных кладбищах, умеет внимательно сверять данные и аккуратно фиксировать результат.',
    ].join('\n'),
  },
  partner_order: {
    title: 'Партнёрский заказ',
    text: [
      'Партнёрский заказ — это категория для крупных и профессиональных проектов, которые iWALY обсуждает с подрядчиками индивидуально.',
      '',
      'Речь может идти не о разовой уборке, а о комплексных работах: благоустройстве участков, установке памятников, мемориальных комплексах, реставрации, строительных или смежных работах на кладбищенской территории.',
      '',
      'Примеры:',
      '• крупное благоустройство захоронений и семейных участков;',
      '• установка памятников, плитки, цоколей, оград и других элементов;',
      '• мемориальные комплексы и нестандартные проекты;',
      '• ремонтные, монтажные, каменные, строительные или ландшафтные работы;',
      '• проекты, где важны опыт, портфолио, сроки и профессиональная ответственность.',
      '',
      'Эта категория рассчитана на профессиональных подрядчиков, бригады, мастерские или специалистов с подтверждённым опытом подобных работ.',
    ].join('\n'),
  },
  volunteer_help: {
    title: 'Помощь волонтёров',
    text: [
      'Помощь волонтёров — особая категория iWALY. Она отличается от обычных услуг по срокам, объёму работ, стоимости и уровню гарантий для заказчиков.',
      '',
      'Иногда человеку важно просто знать, что место памяти близкого не забыто: убрать сухую траву, сделать несколько фотографий, проверить состояние захоронения, помочь с самым простым уходом или поиском, когда полноценный заказ сейчас невозможен.',
      '',
      'В этой категории могут появляться небольшие обращения с минимальной, но разумной стоимостью. При желании заказчик может указать большую сумму — такую, которую считает уместной и справедливой.',
      '',
      'Примеры:',
      '• лёгкая уборка или приведение места в порядок;',
      '• несколько фотографий захоронения;',
      '• проверка состояния участка;',
      '• простая помощь с цветами;',
      '• несложный поиск или ориентирование на кладбище;',
      '• другое небольшое посильное действие.',
      '',
      'Если выбрана только «Помощь волонтёров», регистрация проходит сразу. Это новый, малоизвестный формат: даже после подключения не нужно ожидать большого потока заказов, особенно в регионах. Любая помощь в продвижении приветствуется!',
      '',
      'Это не обычный коммерческий заказ с жёсткими сроками, расширенными требованиями и гарантированным объёмом работ. Это просьба о помощи к человеку, который живёт рядом или бывает в нужном районе и готов откликнуться.',
    ].join('\n'),
  },
};

export default {
  components: {
    Attachment,
    Check,
  },
  data() {
    return {
      payload: [],
      policy: false,
      detailsCategory: null,
      demandStatus: null,
      demandStatusPending: false,
      demandStatusError: false,
      demandStatusRequestSeq: 0,
    };
  },
  validations: {
    policy: {
      checked: (value) => value === true,
    },
    payload: {
      length: (value) => value.filter((obj) => obj.value).length,
    },
  },
  computed: {
    ...mapState({
      serviceCategories: (state) => state.serviceCategories,
      statePayload: (state) => state.payload,
    }),
    checked() {
      return this.payload.reduce((acc, obj) => {
        if (obj.value === true) {
          acc.push(obj.id);
        }

        return acc;
      }, []);
    },
    detailsTitle() {
      const local = this.localServiceInfo(this.detailsCategory);

      return _.get(this.detailsCategory, 'name')
        || _.get(local, 'title')
        || 'Описание услуги';
    },
    detailsText() {
      const local = this.localServiceInfo(this.detailsCategory);

      return _.get(this.detailsCategory, 'description')
        || _.get(this.detailsCategory, 'shortDescription')
        || _.get(this.detailsCategory, 'text')
        || _.get(this.detailsCategory, 'details')
        || _.get(local, 'text')
        || 'Подробное описание для этой категории пока не заполнено.';
    },
    selectedRegionId() {
      return _.get(this.statePayload, 'region.id') || null;
    },
    demandOverall() {
      return _.get(this.demandStatus, 'overall') || null;
    },
    demandSignal() {
      return _.get(this.demandOverall, 'signal') || 'UNKNOWN';
    },
    demandTrafficLightLamps() {
      return this.trafficLightLamps(this.demandSignal);
    },
    demandTitle() {
      if (!this.checked.length) {
        return 'Выберите категории, чтобы увидеть актуальный статус спроса';
      }

      if (!this.selectedRegionId) {
        return 'Сначала выберите регион работы';
      }

      return _.get(this.demandOverall, 'title')
        || 'Статус спроса будет рассчитан автоматически';
    },
    demandMessage() {
      if (!this.checked.length) {
        return [
          'После выбора направления мы проверим ретроспективу за год,',
          'свежую активность, активных исполнителей и общую динамику',
          'по выбранной связке.',
        ].join(' ');
      }

      if (!this.selectedRegionId) {
        return 'Светофор считается по связке регион + категория услуги. Вернитесь назад и укажите регион работы.';
      }

      return _.get(this.demandOverall, 'message')
        || [
          'Мы анализируем спрос по выбранному региону и категориям.',
          'Для коммерческих направлений финальное решение принимается',
          'после проверки локации, опыта и условий работы.',
        ].join(' ');
    },
    demandBadgeText() {
      if (this.demandStatusPending) {
        return 'Анализ спроса';
      }

      return _.get(this.demandOverall, 'badge') || 'Светофор iWALY';
    },
    demandContainerClass() {
      switch (this.demandSignal) {
        case 'RED':
          return 'bg-white border-green-600';
        case 'YELLOW':
          return 'bg-white border-yellow-500';
        case 'GREY':
          return 'bg-white border-geyser';
        case 'GREEN_RESERVE':
          return 'bg-white border-primary';
        default:
          return 'bg-white border-geyser';
      }
    },
    demandBadgeClass() {
      switch (this.demandSignal) {
        case 'RED':
          return 'text-green-700';
        case 'YELLOW':
          return 'text-yellow-700';
        case 'GREY':
          return 'text-muted';
        case 'GREEN_RESERVE':
          return 'text-primary';
        default:
          return 'text-muted';
      }
    },
    demandMetrics() {
      if (!this.demandOverall) {
        return [];
      }

      const orders365 = _.get(this.demandOverall, 'orders_365', 0);
      const orders30 = _.get(this.demandOverall, 'orders_30', 0);
      const activeContractors = _.get(
        this.demandOverall,
        'actually_active_contractors_365',
        _.get(this.demandOverall, 'active_contractors', 0),
      );

      return [
        {
          label: 'Спрос за год',
          value: this.ordersYearLevel(orders365),
        },
        {
          label: 'Последние 30 дней',
          value: this.ordersRecentLevel(orders30),
        },
        {
          label: 'Активные исполнители',
          value: this.contractorsLevel(activeContractors),
        },
      ];
    },
    demandPromotionNote() {
      return [
        'Помощь в информировании людей о сервисе очень ценна,',
        'особенно в удалённых городах региона.',
      ].join(' ');
    },
  },
  methods: {
    ...mapActions({
      SET_STATE: 'ACT_SET_STATE',
    }),

    trafficLightLamps(signal) {
      const activeColor = {
        RED: 'green',
        YELLOW: 'yellow',
        GREEN_RESERVE: 'red',
        GREY: 'grey',
      }[signal] || '';

      return ['red', 'yellow', 'green', 'grey'].map((color) => ({
        key: color,
        className: [
          `iwaly-traffic-light__lamp--${color}`,
          activeColor === color ? 'iwaly-traffic-light__lamp--active' : '',
        ].filter(Boolean).join(' '),
      }));
    },

    ordersYearLevel(value) {
      const count = Number(value || 0);

      if (count <= 0) {
        return 'малозаметный';
      }

      if (count <= 5) {
        return 'единичные заказы';
      }

      if (count <= 30) {
        return 'умеренный';
      }

      return 'заметный';
    },

    ordersRecentLevel(value) {
      const count = Number(value || 0);

      if (count <= 0) {
        return 'малозаметная активность';
      }

      if (count <= 2) {
        return 'единичные заказы';
      }

      if (count <= 10) {
        return 'умеренная активность';
      }

      return 'заметная активность';
    },

    contractorsLevel(value) {
      const count = Number(value || 0);

      if (count <= 0) {
        return 'формируется';
      }

      if (count <= 2) {
        return 'мало';
      }

      if (count <= 8) {
        return 'средне';
      }

      return 'много';
    },

    categoryKey(category) {
      const values = [
        _.get(category, 'code'),
        _.get(category, 'slug'),
        _.get(category, 'alias'),
        _.get(category, 'name'),
      ].join(' ').toLowerCase();

      if (values.indexOf('grave_cleaning') !== -1 || values.indexOf('уборк') !== -1) {
        return 'grave_cleaning';
      }

      if (values.indexOf('funeral_flowers_delivery') !== -1 || values.indexOf('цвет') !== -1) {
        return 'funeral_flowers_delivery';
      }

      if (values.indexOf('improvement_repair') !== -1 || values.indexOf('благоустрой') !== -1) {
        return 'improvement_repair';
      }

      if (values.indexOf('custom_order') !== -1 || values.indexOf('индивидуаль') !== -1) {
        return 'custom_order';
      }

      if (values.indexOf('grave_search') !== -1 || values.indexOf('поиск') !== -1) {
        return 'grave_search';
      }

      if (values.indexOf('partner_order') !== -1 || values.indexOf('партн') !== -1) {
        return 'partner_order';
      }

      if (values.indexOf('volunteer') !== -1 || values.indexOf('волонт') !== -1) {
        return 'volunteer_help';
      }

      return null;
    },

    localServiceInfo(category) {
      const key = this.categoryKey(category);

      if (!key) {
        return null;
      }

      return SERVICE_DESCRIPTIONS[key] || null;
    },

    isVolunteerCategory(category) {
      return this.categoryKey(category) === 'volunteer_help';
    },

    categoryRowClass(category) {
      if (!this.isVolunteerCategory(category)) {
        return '';
      }

      return 'rounded-lg border-2 border-red-300 bg-red-50 px-4 my-2';
    },

    syncCategories(value) {
      const selected = _.get(this.statePayload, 'serviceCategories') || [];
      const hasSavedSelection = selected.length > 0;

      this.payload = value.map((obj) => {
        const isSaved = selected.some(
          (id) => String(id) === String(obj.id),
        );

        return {
          ...obj,
          value: hasSavedSelection
            ? isSaved
            : this.isVolunteerCategory(obj),
        };
      });

      this.persistChecked();
    },

    persistChecked() {
      this.SET_STATE({
        serviceCategories: this.checked,
      });
    },

    checkCategory({ id }, value) {
      this.payload = this.payload.map((obj) => {
        if (obj.id === id) {
          return {
            ...obj,
            value,
          };
        }

        return obj;
      });

      this.$nextTick().then(() => {
        this.persistChecked();
      });
    },

    loadDemandStatus: _.debounce(function loadDemandStatus() {
      const regionId = this.selectedRegionId;
      const categoryIds = this.checked;

      if (!regionId || !categoryIds.length) {
        this.demandStatus = null;
        this.demandStatusError = false;
        this.SET_STATE({
          contractorDemandStatus: null,
          demandSignal: null,
          demandRecommendation: null,
          demandComment: null,
          demandMetrics: null,
        });
        return;
      }

      const requestSeq = this.demandStatusRequestSeq + 1;
      this.demandStatusRequestSeq = requestSeq;
      this.demandStatusPending = true;
      this.demandStatusError = false;

      contractorDemandStatus({ regionId, categoryIds })
        .then((response) => {
          if (requestSeq !== this.demandStatusRequestSeq) {
            return;
          }

          const data = response.data || null;
          this.demandStatus = data;

          const overall = _.get(data, 'overall') || {};

          this.SET_STATE({
            contractorDemandStatus: data,
            demandSignal: _.get(overall, 'signal') || '',
            demandRecommendation: _.get(overall, 'recommendation') || '',
            demandComment: _.get(overall, 'comment') || '',
            demandConnectionMode: _.get(overall, 'connection_mode') || '',
            demandReserveStatus: _.get(overall, 'reserve_status') ? '1' : '0',
            demandMetrics: _.get(data, 'rows') || [],
          });
        })
        .catch(() => {
          if (requestSeq !== this.demandStatusRequestSeq) {
            return;
          }

          this.demandStatus = null;
          this.demandStatusError = true;
          this.SET_STATE({
            contractorDemandStatus: null,
            demandSignal: 'UNKNOWN',
            demandRecommendation: 'Ручная проверка',
            demandComment: 'Статус спроса не был автоматически получен. Заявка будет проверена вручную.',
            demandConnectionMode: 'manual_review',
            demandReserveStatus: '1',
            demandMetrics: [],
          });
        })
        .finally(() => {
          if (requestSeq === this.demandStatusRequestSeq) {
            this.demandStatusPending = false;
          }
        });
    }, 400),

    openDetails(category) {
      this.detailsCategory = category;
      this.$modal.show('service-category-details');
    },

    onSend() {
      this.$v.$touch();

      if (!this.$v.$invalid) {
        this.persistChecked();
        this.$emit('send');
        return;
      }

      if (this.$v.policy.$invalid) {
        this.$iwalyNotify.error({
          text: 'Вы не согласились с условиями документов iWALY.',
        });
      }

      if (this.$v.payload.$invalid) {
        this.$iwalyNotify.error({
          text: 'Выберите хотя бы одну категорию для предоставления услуг.',
        });
      }
    },

    openPublic() {
      window.open('/public', '_blank');
    },

    openLicense() {
      window.open('/license', '_blank');
    },

    openPolicy() {
      window.open('/policy', '_blank');
    },
  },
  mounted() {
    this.loadDemandStatus();
  },

  watch: {
    serviceCategories: {
      handler(value) {
        this.syncCategories(value);
      },
      deep: true,
      immediate: true,
    },
    checked: {
      handler(value) {
        this.SET_STATE({
          serviceCategories: value,
        });
        this.loadDemandStatus();
      },
    },
    selectedRegionId() {
      this.loadDemandStatus();
    },
  },
};
</script>

<style scoped>
.iwaly-traffic-light {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 999px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.08);
  flex-shrink: 0;
}

.iwaly-traffic-light__lamp {
  width: 16px;
  height: 16px;
  border-radius: 999px;
  background: #d1d5db;
  box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.24);
  opacity: 0.38;
}

.iwaly-traffic-light__lamp--red.iwaly-traffic-light__lamp--active {
  background: #ef4444;
  box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.14), 0 0 14px rgba(239, 68, 68, 0.55);
  opacity: 1;
}

.iwaly-traffic-light__lamp--yellow.iwaly-traffic-light__lamp--active {
  background: #f59e0b;
  box-shadow: 0 0 0 4px rgba(245, 158, 11, 0.16), 0 0 14px rgba(245, 158, 11, 0.52);
  opacity: 1;
}

.iwaly-traffic-light__lamp--green.iwaly-traffic-light__lamp--active {
  background: #22c55e;
  box-shadow: 0 0 0 4px rgba(34, 197, 94, 0.14), 0 0 14px rgba(34, 197, 94, 0.48);
  opacity: 1;
}

.iwaly-traffic-light__lamp--grey.iwaly-traffic-light__lamp--active {
  background: #64748b;
  box-shadow: 0 0 0 4px rgba(100, 116, 139, 0.14), 0 0 14px rgba(100, 116, 139, 0.42);
  opacity: 1;
}

.iwaly-demand-metrics {
  margin-top: 4px;
  padding: 14px 16px;
  border-radius: 14px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
}

.iwaly-demand-metrics__title {
  font-size: 12px;
  line-height: 16px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #64748b;
}

.iwaly-demand-metrics__list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
}

.iwaly-demand-metrics__row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
}

.iwaly-demand-metrics__label {
  font-size: 13px;
  line-height: 18px;
  color: #64748b;
}

.iwaly-demand-metrics__value {
  font-size: 14px;
  line-height: 20px;
  font-weight: 700;
  color: #1f2937;
  text-align: right;
}

.iwaly-demand-metrics__note {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid #e5e7eb;
  font-size: 13px;
  line-height: 19px;
  color: #64748b;
}

.iwaly-demand-helper {
  padding: 12px 14px;
  border-radius: 12px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  font-size: 13px;
  line-height: 19px;
  color: #64748b;
}

@media (max-width: 640px) {
  .iwaly-demand-metrics__row {
    display: block;
  }

  .iwaly-demand-metrics__value {
    display: block;
    margin-top: 2px;
    text-align: left;
  }
}
</style>
